// Partner shops are US auto repair shops. Twin of auth/us_shop_address.py; keep the rules
// and messages in sync so the form and the API reject the same addresses the same way.

export const US_STATES = {
    AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
    CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
    FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
    IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
    ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan',
    MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri', MT: 'Montana',
    NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
    NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota',
    OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
    RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee',
    TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington',
    WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};

const STATE_BY_NAME = Object.fromEntries(Object.entries(US_STATES).map(([code, name]) => [name.toLowerCase(), code]));

export const US_ONLY_MESSAGE = 'CarFido shop accounts are available for US shops only.';
export const STATE_INVALID_MESSAGE = 'Enter a valid US state (e.g. CO).';
export const ZIP_INVALID_MESSAGE = 'Enter a valid 5-digit US ZIP code (e.g. 80202).';

// 'co' / 'Colorado' -> 'CO'; anything else -> ''.
export function normalizeUsState(value) {
    const text = String(value || '').trim();
    if (!text) return '';
    if (US_STATES[text.toUpperCase()]) return text.toUpperCase();
    return STATE_BY_NAME[text.toLowerCase()] || '';
}

// '' when the address is a valid US shop address (country blank means US).
export function getUsShopAddressError({ countryCode, region, postalCode }) {
    const country = String(countryCode || 'US').trim().toUpperCase();
    if (country !== 'US') return US_ONLY_MESSAGE;
    if (!normalizeUsState(region)) return STATE_INVALID_MESSAGE;
    if (!isZip5(postalCode)) return ZIP_INVALID_MESSAGE;
    return '';
}

// ── ZIP: exactly five digits, kept as text so leading zeros (07922) survive ──
export const ZIP5_LENGTH = 5;
export const isZip5 = (value) => /^\d{5}$/.test(String(value || '').trim());
// Typing and pasting both go through onChange: keep digits only, at most five.
export const sanitizeZip5 = (value) => String(value || '').replace(/\D/g, '').slice(0, ZIP5_LENGTH);

export const ZIP_REQUIRED_MESSAGE = 'ZIP code is required so drivers can find you.';
export const ZIP_NOT_FOUND_MESSAGE = "We couldn't find that ZIP code in the US. Check it and try again.";
export const ZIP_LOOKUP_UNAVAILABLE_MESSAGE = "We couldn't verify the ZIP code right now. Please try again in a moment.";
export const ZIP_CHECKING_MESSAGE = 'Checking the ZIP code...';
export const CITY_MISMATCH_MESSAGE = "The city doesn't match this ZIP code.";

// ── Shop details (signup Step 2 and Settings) ──────────────────────────────
export const SHOP_NAME_MAX = 120;
export const STREET_MAX = 150;
export const SPECIALTY_MAX = 80;

const squash = (value) => String(value || '').replace(/\s+/g, ' ').trim();
const LETTER = 'A-Za-zÀ-ɏ';
const SHOP_NAME_CHARS = new RegExp(`^[${LETTER}0-9 &'’.,#/()+!@:-]+$`);
const STREET_CHARS = new RegExp(`^[${LETTER}0-9 .,#/'’&()-]+$`);
const SPECIALTY_CHARS = new RegExp(`^[${LETTER}0-9 &'’.,/()+-]+$`);
const HAS_ALNUM = new RegExp(`[${LETTER}0-9]`);
const HAS_LETTER = new RegExp(`[${LETTER}]`);
const LETTERS = new RegExp(`[${LETTER}]`, 'g');

export function shopNameError(value) {
    const name = squash(value);
    if (!name) return 'Shop name is required.';
    if (name.length > SHOP_NAME_MAX) return `Shop name must be ${SHOP_NAME_MAX} characters or fewer.`;
    if (!SHOP_NAME_CHARS.test(name) || !HAS_ALNUM.test(name)) {
        return "Shop name can use letters, numbers, spaces and common punctuation (& ' . , - # / ( ) + ! @ :).";
    }
    return '';
}

// Format only: a well-formed street is not proof that the address exists.
export function streetAddressError(value) {
    const street = squash(value);
    if (!street) return 'Street address is required.';
    if (street.length > STREET_MAX) return `Street address must be ${STREET_MAX} characters or fewer.`;
    if (!STREET_CHARS.test(street) || !HAS_LETTER.test(street)) {
        return "Enter a street address using letters, numbers, spaces and . , # - / ' & ( ).";
    }
    return '';
}

// Primary specialty is free text (there is no fixed option list), so it is checked as a
// short description: required, bounded, made of words.
export function specialtyError(value) {
    const specialty = squash(value);
    if (!specialty) return 'Primary specialty is required.';
    if (specialty.length > SPECIALTY_MAX) return `Primary specialty must be ${SPECIALTY_MAX} characters or fewer.`;
    if (!SPECIALTY_CHARS.test(specialty) || (specialty.match(LETTERS) || []).length < 2) {
        return 'Describe the specialty in words (e.g. General repair & diagnostics).';
    }
    return '';
}

// The accepted spelling of `city` for a looked-up ZIP, or '' if it isn't one of them.
export function matchZipCity(city, zipInfo) {
    const wanted = squash(city).toLowerCase();
    return (zipInfo?.cities || []).find((name) => name.toLowerCase() === wanted) || '';
}

/**
 * Field-by-field errors for the shop details ({} when valid). `zip` is the lookup state
 * from useUsZipLookup; an address that couldn't be verified never passes.
 * Fields: shopName, streetAddress, city, region, postalCode, countryCode, specialty.
 */
export function getShopDetailsErrors(form, zip) {
    const errors = {};
    const name = shopNameError(form.shopName);
    if (name) errors.shopName = name;
    const street = streetAddressError(form.streetAddress);
    if (street) errors.streetAddress = street;
    const specialty = specialtyError(form.specialty);
    if (specialty) errors.specialty = specialty;

    const country = String(form.countryCode || 'US').trim().toUpperCase();
    if (country !== 'US') errors.countryCode = US_ONLY_MESSAGE;

    if (!String(form.postalCode || '').trim()) errors.postalCode = ZIP_REQUIRED_MESSAGE;
    else if (!isZip5(form.postalCode)) errors.postalCode = ZIP_INVALID_MESSAGE;
    else if (zip?.status === 'checking') errors.postalCode = ZIP_CHECKING_MESSAGE;
    else if (zip?.status === 'not_found') errors.postalCode = ZIP_NOT_FOUND_MESSAGE;
    else if (zip?.status !== 'found') errors.postalCode = ZIP_LOOKUP_UNAVAILABLE_MESSAGE;
    else {
        if (normalizeUsState(form.region) !== zip.info.state) errors.region = STATE_INVALID_MESSAGE;
        if (squash(form.city) && !matchZipCity(form.city, zip.info)) errors.city = CITY_MISMATCH_MESSAGE;
    }
    if (!squash(form.city)) errors.city = 'City is required.';
    return errors;
}
